import { type FormEvent, useState } from "react";
import { t } from "i18next";
import { toast } from "sonner";
import {
	Link,
	Navigate,
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useAuth } from "@/lib/auth";

const loginSearchSchema = z.object({
	redirect: z.string().optional(),
});

export const Route = createFileRoute("/login")({
	validateSearch: zodValidator(loginSearchSchema),
	component: LoginPage,
});

export function LoginPage() {
	const { status, user, login } = useAuth();
	const navigate = useNavigate();
	const { redirect } = useSearch({ from: "/login" });

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	if (status === "loading") {
		return null;
	}

	if (status === "authenticated" && user) {
		return <Navigate to={redirect ?? "/dashboard"} replace />;
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsSubmitting(true);
		try {
			const next = await login({ email, password });
			if (next.mustChangePassword) {
				await navigate({ to: "/change-password" });
				return;
			}
			await navigate({ to: redirect ?? "/dashboard" });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t("Login failed");
			toast.error(message);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "background.level1",
				p: 2,
			}}
		>
			<Paper
				component="form"
				onSubmit={handleSubmit}
				sx={{
					width: { xs: "100%", sm: 420 },
					p: { xs: 3, sm: 4 },
					borderRadius: 2,
				}}
			>
				<Stack spacing={3}>
					<Stack spacing={0.5}>
						<Typography variant="h5">{t("Sign in to BattSupport")}</Typography>
						<Typography variant="body2" color="text.secondary">
							{t("Use the email and password provided by your administrator.")}
						</Typography>
					</Stack>
					<TextField
						label={t("Email")}
						type="email"
						autoComplete="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						required
						fullWidth
					/>
					<TextField
						label={t("Password")}
						type="password"
						autoComplete="current-password"
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						required
						fullWidth
					/>
					<Box sx={{ textAlign: "right" }}>
						<MuiLink
							component={Link as React.ElementType}
							to="/forgot-password"
							variant="body2"
							color="text.secondary"
						>
							{t("Forgot password?")}
						</MuiLink>
					</Box>
					<Button
						type="submit"
						variant="contained"
						size="large"
						disabled={isSubmitting}
					>
						{isSubmitting ? t("Signing in…") : t("Sign in")}
					</Button>
				</Stack>
			</Paper>
		</Box>
	);
}
